import {
  Sliders,
  Box,
  Layout,
  Code2,
  Sparkles,
  Gauge,
  Wrench,
  PictureInPicture2,
  ImageIcon,
  Download,
} from 'lucide-react';
import type { CategoryConfig, ChangelogEntry } from './types';

// ---------------------------------------------------------------------------
// Category config
// ---------------------------------------------------------------------------

export const CATEGORIES: CategoryConfig[] = [
  { key: 'parameters', label: 'Parameters', icon: Sliders, filterable: true },
  { key: 'scene', label: 'Scene / 3D', icon: Box, filterable: true },
  { key: 'ui', label: 'UI / UX', icon: Layout, filterable: true },
  { key: 'editor', label: 'Editor', icon: Code2, filterable: true },
  { key: 'ai', label: 'AI', icon: Sparkles, filterable: true },
  { key: 'performance', label: 'Performance', icon: Gauge, filterable: true },
  { key: 'fix', label: 'Fix', icon: Wrench, filterable: true },
  { key: 'pip', label: 'PiP', icon: PictureInPicture2, filterable: false },
  { key: 'textures', label: 'Textures', icon: ImageIcon, filterable: false },
  { key: 'export', label: 'Export', icon: Download, filterable: false },
];

// ---------------------------------------------------------------------------
// Commit counts per day — derived from git log
// ---------------------------------------------------------------------------

export const COMMIT_COUNTS: Record<string, number> = {
  '2026-02-03': 16,
  '2026-02-04': 11,
  '2026-02-05': 9,
  '2026-02-06': 8,
  '2026-02-07': 7,
  '2026-02-08': 9,
  '2026-02-09': 7,
  '2026-02-10': 11,
  '2026-02-11': 12,
  '2026-02-12': 11,
  '2026-02-13': 10,
  '2026-02-14': 9,
  '2026-02-15': 3,
  '2026-02-16': 2,
  '2026-02-17': 4,
  '2026-02-18': 4,
  '2026-02-19': 5,
  '2026-02-20': 5,
  '2026-02-21': 13,
  '2026-02-22': 4,
  '2026-02-23': 4,
  '2026-02-24': 6,
  '2026-02-25': 13,
  '2026-02-26': 7,
  '2026-02-27': 2,
  '2026-02-28': 3,
  '2026-03-01': 12,
  '2026-03-02': 3,
  '2026-03-03': 2,
  '2026-03-04': 3,
  '2026-03-05': 4,
  '2026-03-06': 13,
  '2026-03-07': 8,
  '2026-03-08': 3,
  '2026-03-09': 7,
  '2026-03-10': 7,
  '2026-03-11': 9,
  '2026-03-12': 20,
  '2026-03-13': 2,
  '2026-03-14': 9,
  '2026-03-15': 14,
  '2026-03-16': 3,
  '2026-03-17': 7,
  '2026-03-18': 11,
  '2026-03-19': 4,
  '2026-03-20': 7,
  '2026-03-21': 5,
  '2026-03-22': 14,
  '2026-03-23': 7,
  '2026-03-24': 7,
  '2026-03-25': 5,
  '2026-03-26': 3,
  '2026-03-27': 3,
  '2026-03-28': 3,
  '2026-03-29': 2,
  '2026-03-30': 4,
  '2026-03-31': 3,
  '2026-04-01': 7,
  '2026-04-02': 4,
  '2026-04-03': 2,
  '2026-04-04': 4,
  '2026-04-05': 10,
  '2026-04-06': 5,
  '2026-04-07': 8,
  '2026-04-08': 11,
  '2026-04-09': 1,
  '2026-04-10': 9,
  '2026-04-11': 2,
  '2026-04-12': 9,
  '2026-04-13': 5,
  '2026-04-14': 2,
  '2026-04-15': 10,
  '2026-04-16': 2,
  '2026-04-17': 8,
  '2026-04-18': 1,
  '2026-04-19': 1,
  '2026-04-20': 4,
  '2026-04-21': 4,
  '2026-04-22': 2,
  '2026-04-23': 5,
  '2026-04-24': 4,
  '2026-04-25': 10,
  '2026-04-26': 5,
  '2026-04-27': 1,
  '2026-04-28': 2,
  '2026-04-29': 7,
  '2026-04-30': 8,
  '2026-05-01': 4,
  '2026-05-02': 4,
  '2026-05-03': 6,
  '2026-05-04': 3,
  '2026-05-05': 5,
  '2026-05-06': 6,
  '2026-05-07': 4,
  '2026-05-08': 3,
  '2026-05-09': 14,
  '2026-05-10': 8,
  '2026-05-11': 5,
  '2026-05-12': 3,
  '2026-05-13': 2,
};

// ---------------------------------------------------------------------------
// Changelog entries — each item tagged with a category
// ---------------------------------------------------------------------------

export const CHANGELOG: ChangelogEntry[] = [
  {
    date: '2026-05-13',
    commitCount: COMMIT_COUNTS['2026-05-13'],
    items: [
      {
        text: 'Add snapshot download context menu',
        category: 'parameters',
      },
    ],
  },
  {
    date: '2026-05-12',
    commitCount: COMMIT_COUNTS['2026-05-12'],
    items: [
      {
        text: 'Add product readiness score to the Product Mode button',
        category: 'ui',
      },
    ],
  },
  {
    date: '2026-05-11',
    commitCount: COMMIT_COUNTS['2026-05-11'],
    items: [
      {
        text: 'Improve UI',
        category: 'fix',
      },
    ],
  },
  {
    date: '2026-05-10',
    commitCount: COMMIT_COUNTS['2026-05-10'],
    items: [
      {
        text: 'Add gobo light projection maps',
        category: 'scene',
      },
      {
        text: 'Improve animated lights pipeline',
        category: 'scene',
      },
    ],
  },
  {
    date: '2026-05-09',
    commitCount: COMMIT_COUNTS['2026-05-09'],
    items: [
      {
        text: 'Add examples slideshow browser',
        category: 'ui',
      },
      {
        text: 'Add 20 built-in examples covering parameters, viewer, lighting, cameras, and motion',
        category: 'editor',
      },
    ],
  },
  {
    date: '2026-05-08',
    commitCount: COMMIT_COUNTS['2026-05-08'],
    items: [
      {
        text: 'Add tag-based filtering to the blog index',
        category: 'ui',
      },
    ],
  },
  {
    date: '2026-05-07',
    commitCount: COMMIT_COUNTS['2026-05-07'],
    items: [
      {
        text: 'Add table of contents to blog posts with active-section highlighting',
        category: 'ui',
      },
    ],
  },
  {
    date: '2026-05-06',
    commitCount: COMMIT_COUNTS['2026-05-06'],
    items: [
      {
        text: 'Add smart compile-skip',
        category: 'performance',
      },
      {
        text: 'Add replay button to viewer toolbar when an intro camera path is defined',
        category: 'scene',
      },
    ],
  },
  {
    date: '2026-05-05',
    commitCount: COMMIT_COUNTS['2026-05-05'],
    items: [
      {
        text: 'Add visual diagrams to SCAD toolbar feature dialogs',
        category: 'editor',
      },
    ],
  },
  {
    date: '2026-05-04',
    commitCount: COMMIT_COUNTS['2026-05-04'],
    items: [
      {
        text: 'Fix shortcuts when in code editor',
        category: 'editor',
      },
      {
        text: 'Add slash mode autocompletes with hints to command palette',
        category: 'editor',
      },
    ],
  },
  {
    date: '2026-05-03',
    commitCount: COMMIT_COUNTS['2026-05-03'],
    items: [
      {
        text: 'Add informative empty state views',
        category: 'editor',
      },
      {
        text: 'Reorganize code panel menus',
        category: 'editor',
      },
      {
        text: 'Update command palette',
        category: 'editor',
      },
    ],
  },
  {
    date: '2026-05-02',
    commitCount: COMMIT_COUNTS['2026-05-02'],
    items: [
      {
        text: 'Add JS SVG parser and WASM compile pipeline integration',
        category: 'editor',
      },
    ],
  },
  {
    date: '2026-05-01',
    commitCount: COMMIT_COUNTS['2026-05-01'],
    items: [
      {
        text: 'Add code expression parameter type — inline OpenSCAD expressions with // [code]',
        category: 'parameters',
      },
      {
        text: 'Add code combobox — preset expressions selectable from a dropdown with free-text override',
        category: 'parameters',
      },
    ],
  },
  {
    date: '2026-04-30',
    commitCount: COMMIT_COUNTS['2026-04-30'],
    items: [
      {
        text: 'Turn number fields into spinners with up/down buttons',
        category: 'parameters',
      },
      {
        text: 'Add spinners to vector fields and slider labels',
        category: 'parameters',
      },
      {
        text: 'Add custom unit labels (unit=) for number parameters',
        category: 'parameters',
      },
      { text: 'Fix labeled dropdown parsing', category: 'fix' },
    ],
  },
  {
    date: '2026-04-29',
    commitCount: COMMIT_COUNTS['2026-04-29'],
    items: [
      {
        text: 'Support OpenSCAD native bracket dropdown syntax [value:Label]',
        category: 'parameters',
      },
      {
        text: 'Use textareas for multi-line string parameters',
        category: 'parameters',
      },
      {
        text: 'Add parameter code dropdown menu for inserting param templates',
        category: 'editor',
      },
    ],
  },
  {
    date: '2026-04-28',
    commitCount: COMMIT_COUNTS['2026-04-28'],
    items: [
      {
        text: 'Add stack input — a reorderable list parameter type',
        category: 'parameters',
      },
    ],
  },
  {
    date: '2026-04-27',
    commitCount: COMMIT_COUNTS['2026-04-27'],
    items: [
      { text: 'Add blank files with init templates', category: 'editor' },
    ],
  },
  {
    date: '2026-04-26',
    commitCount: COMMIT_COUNTS['2026-04-26'],
    items: [
      { text: 'Add @pubsub parameter annotation', category: 'parameters' },
      {
        text: 'Add pub/sub subscribe API for texture and shader scripts',
        category: 'textures',
      },
    ],
  },
  {
    date: '2026-04-25',
    commitCount: COMMIT_COUNTS['2026-04-25'],
    items: [
      {
        text: 'Add shader_material module for custom GLSL shaders',
        category: 'scene',
      },
      { text: 'Add shader script preview in the files panel', category: 'ui' },
      {
        text: 'Improve landing page with cheatsheet overview and API extensions',
        category: 'ui',
      },
    ],
  },
  {
    date: '2026-04-24',
    commitCount: COMMIT_COUNTS['2026-04-24'],
    items: [
      { text: 'Add clone() module for instancing geometry', category: 'scene' },
    ],
  },
  {
    date: '2026-04-23',
    commitCount: COMMIT_COUNTS['2026-04-23'],
    items: [
      {
        text: 'Add color indicator bar to the code editor',
        category: 'editor',
      },
    ],
  },
  {
    date: '2026-04-22',
    commitCount: COMMIT_COUNTS['2026-04-22'],
    items: [{ text: 'Fix recompile bug with included files', category: 'fix' }],
  },
  {
    date: '2026-04-21',
    commitCount: COMMIT_COUNTS['2026-04-21'],
    items: [
      { text: 'Add animated light properties with LFOs', category: 'scene' },
      {
        text: 'Add new motion module for time-based animations',
        category: 'scene',
      },
    ],
  },
  {
    date: '2026-04-20',
    commitCount: COMMIT_COUNTS['2026-04-20'],
    items: [
      {
        text: 'Add light orbits — make lights orbit around the model',
        category: 'scene',
      },
    ],
  },
  {
    date: '2026-04-18',
    commitCount: COMMIT_COUNTS['2026-04-18'],
    items: [{ text: 'Add randomness property to PiPs', category: 'pip' }],
  },
  {
    date: '2026-04-17',
    commitCount: COMMIT_COUNTS['2026-04-17'],
    items: [
      { text: 'Add SVG overlay custom module', category: 'scene' },
      { text: 'Add PiP fixed-value positioning system', category: 'pip' },
      { text: 'Add face property to JS texture scripts', category: 'textures' },
    ],
  },
  {
    date: '2026-04-16',
    commitCount: COMMIT_COUNTS['2026-04-16'],
    items: [
      {
        text: 'Add interactive_publish for cross-parameter event broadcasting',
        category: 'parameters',
      },
    ],
  },
  {
    date: '2026-04-15',
    commitCount: COMMIT_COUNTS['2026-04-15'],
    items: [
      { text: 'Add support for irregular flex grids', category: 'parameters' },
      { text: 'Add optional props to texture scripts', category: 'textures' },
      { text: 'Add pub/sub messaging to JS textures', category: 'textures' },
    ],
  },
  {
    date: '2026-04-14',
    commitCount: COMMIT_COUNTS['2026-04-14'],
    items: [
      {
        text: 'Add ability to pre-lock parameters from code',
        category: 'parameters',
      },
    ],
  },
  {
    date: '2026-04-13',
    commitCount: COMMIT_COUNTS['2026-04-13'],
    items: [
      {
        text: 'Improve parameter randomizer and add ability to lock parameters',
        category: 'parameters',
      },
      {
        text: 'Add proportional value change to parameters',
        category: 'parameters',
      },
    ],
  },
  {
    date: '2026-04-12',
    commitCount: COMMIT_COUNTS['2026-04-12'],
    items: [
      { text: 'Improve PiP drag placement', category: 'pip' },
      { text: 'Add resize handle to PiPs', category: 'pip' },
      {
        text: 'Add ability to align PiPs to the right and bottom',
        category: 'pip',
      },
      {
        text: 'Add new magic variable-to-variable feature',
        category: 'parameters',
      },
      {
        text: 'Add fast color toggle to color swatches',
        category: 'parameters',
      },
    ],
  },
  {
    date: '2026-04-11',
    commitCount: COMMIT_COUNTS['2026-04-11'],
    items: [{ text: 'Add category filter to blog posts', category: 'ui' }],
  },
  {
    date: '2026-04-10',
    commitCount: COMMIT_COUNTS['2026-04-10'],
    items: [
      { text: 'Add new scene_camera custom module', category: 'scene' },
      { text: 'Add new constraints warning feature', category: 'ui' },
    ],
  },
  {
    date: '2026-04-09',
    commitCount: COMMIT_COUNTS['2026-04-09'],
    items: [
      {
        text: 'Complete API reference and link it from footer',
        category: 'editor',
      },
    ],
  },
  {
    date: '2026-04-08',
    commitCount: COMMIT_COUNTS['2026-04-08'],
    items: [
      { text: 'Add terms and pricing page', category: 'ui' },
      { text: 'Add footer with contact form', category: 'ui' },
      { text: 'Add more PiP animation types and easings', category: 'pip' },
      { text: 'Make PiP animations combinable', category: 'pip' },
    ],
  },
  {
    date: '2026-04-07',
    commitCount: COMMIT_COUNTS['2026-04-07'],
    items: [
      { text: 'Add interactive parameter cheatsheet page', category: 'ui' },
      { text: 'Add live parser tester to cheatsheet page', category: 'editor' },
    ],
  },
  {
    date: '2026-04-06',
    commitCount: COMMIT_COUNTS['2026-04-06'],
    items: [
      {
        text: 'Add a sophisticated polygon parameter input',
        category: 'parameters',
      },
    ],
  },
  {
    date: '2026-04-05',
    commitCount: COMMIT_COUNTS['2026-04-05'],
    items: [
      {
        text: 'Add XYZ pad — a 3-axis drag control for vector parameters',
        category: 'parameters',
      },
      {
        text: 'Add grid input — pick values from an interactive grid',
        category: 'parameters',
      },
    ],
  },
  {
    date: '2026-04-04',
    commitCount: COMMIT_COUNTS['2026-04-04'],
    items: [
      {
        text: 'Add stepper input — click buttons to nudge a value up or down',
        category: 'parameters',
      },
      {
        text: 'Add XY pad — drag in 2D to control two values at once',
        category: 'parameters',
      },
      {
        text: 'Add angle dial — rotate a visual dial to set an angle',
        category: 'parameters',
      },
    ],
  },
  {
    date: '2026-04-02',
    commitCount: COMMIT_COUNTS['2026-04-02'],
    items: [
      {
        text: 'Add annotations — attach floating labels to any point in your 3D scene',
        category: 'scene',
      },
    ],
  },
  {
    date: '2026-04-01',
    commitCount: COMMIT_COUNTS['2026-04-01'],
    items: [
      {
        text: 'Add SVG and PNG texture support — use image files directly as surface textures',
        category: 'textures',
      },
    ],
  },
  {
    date: '2026-03-30',
    commitCount: COMMIT_COUNTS['2026-03-30'],
    items: [
      {
        text: 'Add model interaction — click objects in the 3D scene to trigger parameter changes',
        category: 'scene',
      },
    ],
  },
  {
    date: '2026-03-29',
    commitCount: COMMIT_COUNTS['2026-03-29'],
    items: [
      {
        text: 'Add SVG group input — pick individual SVG groups as a parameter value',
        category: 'parameters',
      },
    ],
  },
  {
    date: '2026-03-26',
    commitCount: COMMIT_COUNTS['2026-03-26'],
    items: [
      {
        text: 'Add polygon mode — view your model as a flat polygon mesh',
        category: 'scene',
      },
      { text: 'Add more keyboard shortcuts', category: 'editor' },
    ],
  },
  {
    date: '2026-03-25',
    commitCount: COMMIT_COUNTS['2026-03-25'],
    items: [
      {
        text: 'Add collision detection to the printability checker',
        category: 'scene',
      },
    ],
  },
  {
    date: '2026-03-24',
    commitCount: COMMIT_COUNTS['2026-03-24'],
    items: [
      {
        text: 'Add tabbed parameter groups — organize parameters into tabs in the sidebar',
        category: 'parameters',
      },
      {
        text: 'Add labels and display modes (grayscale, wireframe) to Picture-in-Picture cameras',
        category: 'pip',
      },
    ],
  },
  {
    date: '2026-03-23',
    commitCount: COMMIT_COUNTS['2026-03-23'],
    items: [
      { text: 'Add dynamic parameter limits', category: 'parameters' },
      {
        text: 'Improve parameters with AI — let AI suggest better parameter definitions for your model',
        category: 'ai',
      },
    ],
  },
  {
    date: '2026-03-22',
    commitCount: COMMIT_COUNTS['2026-03-22'],
    items: [
      {
        text: 'Add visibility expressions for conditional parameter display',
        category: 'parameters',
      },
      {
        text: 'Add markdown and expression support in parameters panel',
        category: 'parameters',
      },
      { text: 'Add extended range sliders', category: 'parameters' },
    ],
  },
  {
    date: '2026-03-21',
    commitCount: COMMIT_COUNTS['2026-03-21'],
    items: [
      {
        text: 'Add snapshot and parameter selection from Markdown links',
        category: 'parameters',
      },
      { text: 'Add markdown block types', category: 'parameters' },
      { text: 'Add copy as snapshot', category: 'parameters' },
      {
        text: 'Add parameter groups closed by default option',
        category: 'parameters',
      },
    ],
  },
  {
    date: '2026-03-20',
    commitCount: COMMIT_COUNTS['2026-03-20'],
    items: [
      {
        text: 'Add predefined color options definable in code',
        category: 'parameters',
      },
      { text: 'Add code-definable snapshots', category: 'parameters' },
      {
        text: 'Add markdown support in parameters panel',
        category: 'parameters',
      },
      { text: 'Add PiP in parameters panel', category: 'pip' },
    ],
  },
  {
    date: '2026-03-19',
    commitCount: COMMIT_COUNTS['2026-03-19'],
    items: [
      { text: 'Add PiP drag & drop', category: 'pip' },
      { text: 'Add PiP context menu', category: 'pip' },
      { text: 'Make PiPs clickable', category: 'pip' },
    ],
  },
  {
    date: '2026-03-18',
    commitCount: COMMIT_COUNTS['2026-03-18'],
    items: [
      { text: 'Add improve with AI', category: 'ai' },
      { text: 'Add object inspector', category: 'scene' },
      { text: 'Add grayscale mode', category: 'scene' },
      { text: 'Add worker performance stats', category: 'performance' },
      { text: 'Add direct PiP from scene camera', category: 'pip' },
    ],
  },
  {
    date: '2026-03-17',
    commitCount: COMMIT_COUNTS['2026-03-17'],
    items: [
      { text: 'Add WebMCP support', category: 'editor' },
      { text: 'Add string and hex color support', category: 'parameters' },
      { text: 'Add favorite colors to color picker', category: 'parameters' },
    ],
  },
  {
    date: '2026-03-16',
    commitCount: COMMIT_COUNTS['2026-03-16'],
    items: [
      { text: 'Add command palette', category: 'editor' },
      { text: 'Extend code toolbar options', category: 'editor' },
    ],
  },
  {
    date: '2026-03-15',
    commitCount: COMMIT_COUNTS['2026-03-15'],
    items: [
      {
        text: 'Add Picture-in-Picture (PiP) cameras from code',
        category: 'pip',
      },
    ],
  },
  {
    date: '2026-03-14',
    commitCount: COMMIT_COUNTS['2026-03-14'],
    items: [
      {
        text: 'Add JS canvas texture support for texture scripts',
        category: 'textures',
      },
      {
        text: 'Add parameter support for texture scripts',
        category: 'textures',
      },
      { text: 'Add animated texture preview', category: 'textures' },
    ],
  },
  {
    date: '2026-03-13',
    commitCount: COMMIT_COUNTS['2026-03-13'],
    items: [{ text: 'Add new scene grids', category: 'scene' }],
  },
  {
    date: '2026-03-12',
    commitCount: COMMIT_COUNTS['2026-03-12'],
    items: [
      { text: 'Add scene background method', category: 'scene' },
      { text: 'Add subtle animated app background', category: 'ui' },
    ],
  },
  {
    date: '2026-03-11',
    commitCount: COMMIT_COUNTS['2026-03-11'],
    items: [
      { text: 'Add STL preview from files', category: 'export' },
      { text: 'Add Markdown rendering to the AI explain mode', category: 'ai' },
    ],
  },
  {
    date: '2026-03-10',
    commitCount: COMMIT_COUNTS['2026-03-10'],
    items: [
      { text: 'Add split SVG feature', category: 'export' },
      { text: 'Add mesh to file system', category: 'export' },
      { text: 'Make files in file panel editable', category: 'export' },
      { text: 'Add extract modules to files', category: 'export' },
    ],
  },
  {
    date: '2026-03-09',
    commitCount: COMMIT_COUNTS['2026-03-09'],
    items: [
      { text: 'Add support for JSON imports', category: 'export' },
      { text: 'Add JSON API support', category: 'editor' },
    ],
  },
  {
    date: '2026-03-08',
    commitCount: COMMIT_COUNTS['2026-03-08'],
    items: [
      {
        text: 'Use a four worker pool for color compilation',
        category: 'performance',
      },
      { text: 'Fix fit to bounds', category: 'fix' },
      { text: 'Fix high quality downloads', category: 'fix' },
    ],
  },
  {
    date: '2026-03-07',
    commitCount: COMMIT_COUNTS['2026-03-07'],
    items: [
      { text: 'Add high quality export', category: 'export' },
      {
        text: 'Only compile if there are no syntax errors',
        category: 'performance',
      },
      {
        text: 'Add each color to the scene right when available',
        category: 'performance',
      },
    ],
  },
  {
    date: '2026-03-06',
    commitCount: COMMIT_COUNTS['2026-03-06'],
    items: [
      { text: 'Add refactoring feature', category: 'editor' },
      { text: 'Improve selection handling', category: 'editor' },
    ],
  },
  {
    date: '2026-03-05',
    commitCount: COMMIT_COUNTS['2026-03-05'],
    items: [{ text: 'Add Gridfinity library', category: 'editor' }],
  },
  {
    date: '2026-03-04',
    commitCount: COMMIT_COUNTS['2026-03-04'],
    items: [
      { text: 'Add font input parameter type', category: 'parameters' },
      { text: 'Add multi view', category: 'ui' },
    ],
  },
  {
    date: '2026-03-02',
    commitCount: COMMIT_COUNTS['2026-03-02'],
    items: [{ text: 'Add blog with guides and tutorials', category: 'ui' }],
  },
  {
    date: '2026-03-01',
    commitCount: COMMIT_COUNTS['2026-03-01'],
    items: [
      { text: 'Add landing page', category: 'ui' },
      {
        text: 'Add Stripe integration for paid model downloads',
        category: 'ui',
      },
    ],
  },
  {
    date: '2026-02-28',
    commitCount: COMMIT_COUNTS['2026-02-28'],
    items: [
      {
        text: 'Add tilde (~) modifier to exclude geometry from export',
        category: 'export',
      },
      { text: 'Report cursor position in editor', category: 'editor' },
      { text: 'Add better error reporting', category: 'editor' },
      { text: 'Add compilation web worker caching', category: 'performance' },
    ],
  },
  {
    date: '2026-02-27',
    commitCount: COMMIT_COUNTS['2026-02-27'],
    items: [
      { text: 'Add AI explain mode', category: 'ai' },
      { text: 'Add extract modules to files feature', category: 'export' },
    ],
  },
  {
    date: '2026-02-26',
    commitCount: COMMIT_COUNTS['2026-02-26'],
    items: [
      {
        text: 'Add SCAD toolbar with dialogs for shapes, clones, matrix, hull, minkowski, modifiers, text, and render settings',
        category: 'editor',
      },
      {
        text: 'Add most used library imports from toolbar',
        category: 'editor',
      },
      { text: 'Visualize light sources in the scene', category: 'scene' },
    ],
  },
  {
    date: '2026-02-25',
    commitCount: COMMIT_COUNTS['2026-02-25'],
    items: [
      { text: 'Add all standard Three.js light sources', category: 'scene' },
      { text: 'Add printability check to download button', category: 'ui' },
      { text: 'Add throttled realtime sliders', category: 'parameters' },
    ],
  },
  {
    date: '2026-02-24',
    commitCount: COMMIT_COUNTS['2026-02-24'],
    items: [
      { text: 'Add undo/redo', category: 'editor' },
      {
        text: 'Add Range parameter input for OpenSCAD range vectors',
        category: 'parameters',
      },
      { text: 'Add custom light sources with light_spot()', category: 'scene' },
      { text: 'Add exclude_from_export support', category: 'export' },
    ],
  },
  {
    date: '2026-02-23',
    commitCount: COMMIT_COUNTS['2026-02-23'],
    items: [
      { text: 'Add file upload parameter type', category: 'parameters' },
      { text: 'Add inline error reporting in editor', category: 'editor' },
    ],
  },
  {
    date: '2026-02-22',
    commitCount: COMMIT_COUNTS['2026-02-22'],
    items: [
      {
        text: 'Add named vector parameters and limits support',
        category: 'parameters',
      },
      { text: 'Export parameters on STL export', category: 'export' },
      { text: 'Add parameters import', category: 'parameters' },
    ],
  },
  {
    date: '2026-02-21',
    commitCount: COMMIT_COUNTS['2026-02-21'],
    items: [
      {
        text: 'Run Monaco editor in a web worker for better performance',
        category: 'performance',
      },
      {
        text: 'Add performance monitoring and FPS display',
        category: 'performance',
      },
    ],
  },
  {
    date: '2026-02-20',
    commitCount: COMMIT_COUNTS['2026-02-20'],
    items: [
      { text: 'Add performance mode toggle', category: 'performance' },
      { text: 'Add $fn to parameters', category: 'parameters' },
      { text: 'Add product mode', category: 'ui' },
      { text: 'Add optional price to download button', category: 'ui' },
      { text: 'Add a help system', category: 'ui' },
    ],
  },
  {
    date: '2026-02-19',
    commitCount: COMMIT_COUNTS['2026-02-19'],
    items: [
      { text: 'Add measurement tool', category: 'scene' },
      { text: 'Add advanced scene settings', category: 'scene' },
      { text: 'Add screenshot feature', category: 'ui' },
      { text: 'Add fit to bounds feature', category: 'scene' },
    ],
  },
  {
    date: '2026-02-18',
    commitCount: COMMIT_COUNTS['2026-02-18'],
    items: [
      { text: 'Add physical material (PBR)', category: 'scene' },
      { text: 'Add soft shadow toggle', category: 'scene' },
      { text: 'Add the Lego library', category: 'editor' },
    ],
  },
  {
    date: '2026-02-17',
    commitCount: COMMIT_COUNTS['2026-02-17'],
    items: [
      { text: 'Add animated moving light source', category: 'scene' },
      { text: 'Add panel collapsing', category: 'ui' },
      { text: 'Add presentation mode', category: 'ui' },
      { text: 'Add SSAO (Ambient Occlusion) with toggle', category: 'scene' },
    ],
  },
  {
    date: '2026-02-16',
    commitCount: COMMIT_COUNTS['2026-02-16'],
    items: [
      {
        text: 'Create by prompt — AI code generation from plain English',
        category: 'ai',
      },
    ],
  },
  {
    date: '2026-02-15',
    commitCount: COMMIT_COUNTS['2026-02-15'],
    items: [
      { text: 'Support ghost objects and transparency', category: 'scene' },
      { text: 'Add world axis display', category: 'scene' },
      { text: 'Add background color picker', category: 'ui' },
      {
        text: 'Add wireframe view and solid-to-wireframe slider',
        category: 'scene',
      },
    ],
  },
  {
    date: '2026-02-14',
    commitCount: COMMIT_COUNTS['2026-02-14'],
    items: [{ text: 'Add file import functionality', category: 'export' }],
  },
  {
    date: '2026-02-13',
    commitCount: COMMIT_COUNTS['2026-02-13'],
    items: [
      { text: 'Add randomize functionality', category: 'parameters' },
      { text: 'Add config snapshots', category: 'parameters' },
      {
        text: 'Add support for popular OpenSCAD libraries (BOSL2, BOSL, MCAD)',
        category: 'editor',
      },
      {
        text: 'Add auto parameter icons and humanized labels',
        category: 'parameters',
      },
    ],
  },
  {
    date: '2026-02-04',
    commitCount: COMMIT_COUNTS['2026-02-04'],
    items: [
      { text: 'Initial launch', category: 'ui' },
      { text: 'Add color support and multi-color export', category: 'export' },
    ],
  },
];
